import { creatorOrderV2Api } from '@/apis/creatorOrderV2/creatorOrderV2Api';
import { isCreatorOrderV2ApiError } from '@/apis/creatorOrderV2/errors';
import { adaptCreatorOrderDetailV2 } from '@/biz/multiPlatformCollaboration/productOrderAdapter';
import { useMultiplePricesStore } from '@/hooks/useMultiplePricesStore';
import { useWebModalStore } from '@/hooks/useWebModalStore';
import i18n from '@/i18n';
import { growthbook } from '@/index';
import toast from 'react-hot-toast';
import { create } from 'zustand';
import { useCreatorDeliveryTimelineStore } from './useCreatorDeliveryTimelineStore';

let detailRequestVersion = 0;
const reQueryDetailData = async ({
  orderId,
  validate,
  clearDateAndUpdate = false,
  setMyRate = false
}: {
  orderId: string;
  validate?: boolean;
  clearDateAndUpdate?: boolean;
  setMyRate?: boolean;
}) => {
  const request = ++detailRequestVersion;
  let nativeRequest = false;
  try {
    window.localStorage.setItem('auth_redirect', `/campaign/detail/${orderId}`);
    window.localStorage.setItem('homepage', `/campaign/detail/${orderId}`);
    if (setMyRate) {
      useDetailDataStore.setState({ myRateLoading: true });
    } else {
      useDetailDataStore.setState({ isLoadingData: true });
    }
    if (clearDateAndUpdate) {
      // 重新进入 置空
      useDetailDataStore.setState({ detailData: {} });
    }
    nativeRequest = true;
    useCreatorDeliveryTimelineStore.getState().beginRefresh(orderId);
    const [detailResponse] = await Promise.allSettled([
      creatorOrderV2Api.detail(orderId, validate)
    ]);
    if (request !== detailRequestVersion) return;

    // 处理订单详情
    if (detailResponse.status === 'fulfilled') {
      const detailData = adaptCreatorOrderDetailV2(detailResponse.value.data);

      if (!detailData.order) {
        toast.error(
          i18n.t('order.detail.not_found_error', {
            defaultValue:
              'Unable to find this order, please contact customer service.'
          })
        );
        return;
      } else {
        // 获取
        getGrowthbookFlag(detailData);
        console.log(
          'detailData.order.priceFields',
          detailData.order.priceFields
        );
        const priceFields = Array.isArray(detailData.order.priceFields)
          ? detailData.order.priceFields.map((priceField: any) => ({
              ...priceField,
              // Used by the existing quote UI as the immutable comparison
              // value. Do not mutate the adapter/API response in place.
              originPrice: priceField.price
            }))
          : [];
        detailData.order = { ...detailData.order, priceFields };

        // 设置默认选择 index 0 的价格
        const { selectedPriceFields } = useMultiplePricesStore.getState();
        useMultiplePricesStore.setState({
          selectedPriceFields:
            selectedPriceFields.length > 0
              ? selectedPriceFields
              : priceFields[0]
                ? [priceFields[0]]
                : []
        });
      }

      useDetailDataStore.setState({ detailData });
      await useCreatorDeliveryTimelineStore.getState().load(orderId);
      return detailData;
    } else {
      console.log('detailResponse', detailResponse);
      console.error('Failed to get order details:', detailResponse.reason);
      const reason = detailResponse.reason;
      const denied =
        isCreatorOrderV2ApiError(reason) &&
        [40001, 40004, 40011, 47011, 47012].includes(reason.code);
      const currentOrder = useDetailDataStore.getState().detailData?.order;
      const sameOrder =
        String(
          currentOrder?.orderId || currentOrder?.id || currentOrder?._id || ''
        ) === orderId;
      // Preserve an open form on transient refresh failure, but disable stale actions.
      if (denied || !sameOrder) useDetailDataStore.setState({ detailData: {} });
      useCreatorDeliveryTimelineStore.setState({
        loading: false,
        error:
          reason instanceof Error
            ? reason
            : new Error('Unable to refresh order.'),
        ...(denied ? { timeline: null, writesDisabled: true } : {})
      });
      if (isCreatorOrderV2ApiError(reason)) {
        if (reason.recoveryAction === 'DENY_ACCESS') {
          const ownerEmail = String(reason.payload?.ownerEmail || '');
          toast.error(
            ownerEmail
              ? i18n.t('order.detail.forbidden_error_2', {
                  defaultValue:
                    'This deal is associated with the account registered under the email {ownerEmail}. Please sign in with this account to access the deal details.',
                  ownerEmail
                })
              : i18n.t('order.detail.forbidden_error', {
                  defaultValue: 'Your session has expired. Please log in again.'
                })
          );
        } else if (
          reason.recoveryAction === 'EXIT_DETAIL' &&
          reason.payload?.deletedReason
        ) {
          useWebModalStore.setState({
            showModalSoftDeleteTip: true,
            softDeleteReason: String(reason.payload.deletedReason)
          });
        } else {
          toast.error(
            reason.message ||
              i18n.t('order.detail.fetch_error', {
                defaultValue:
                  'This collaboration has ended and is no longer valid. For any questions, please contact our support team.'
              })
          );
        }
      } else if (reason.message === '401 Unauthorized') {
        toast.error(
          i18n.t('order.detail.unauthorized_error', {
            defaultValue: '401 Unauthorized. Please login again.'
          })
        );
      } else if (reason.message === '403 Forbidden') {
        const { payload } = reason;
        if (payload?.ownerEmail) {
          toast.error(
            i18n.t('order.detail.forbidden_error_2', {
              defaultValue:
                'This deal is associated with the account registered under the email {ownerEmail}. Please sign in with this account to access the deal details.',
              ownerEmail: payload?.ownerEmail
            })
          );
        } else {
          toast.error(
            i18n.t('order.detail.forbidden_error', {
              defaultValue: 'Your session has expired. Please log in again.'
            })
          );
        }
      } else if (
        reason.message === 'order not found' &&
        reason.payload?.deletedReason
      ) {
        useWebModalStore?.setState({
          showModalSoftDeleteTip: true,
          softDeleteReason: reason.payload?.deletedReason || ''
        });
      } else {
        toast.error(
          i18n.t('order.detail.fetch_error', {
            defaultValue:
              'This collaboration has ended and is no longer valid. For any questions, please contact our support team.'
          })
        );
      }
    }
  } catch (e: any) {
    if (request !== detailRequestVersion) return;
    if (nativeRequest)
      useCreatorDeliveryTimelineStore.setState({
        loading: false,
        error: e instanceof Error ? e : new Error('Unable to refresh order.')
      });
    toast.error(
      e.message ||
        i18n.t('order.detail.generic_error', {
          defaultValue: 'Failed to get data.'
        })
    );
  } finally {
    if (request !== detailRequestVersion) return;
    if (setMyRate) {
      useDetailDataStore.setState({ myRateLoading: false });
    } else {
      useDetailDataStore.setState({ isLoadingData: false });
    }
  }
};

type DetailDataStore = {
  detailData: any;
  setDetailData: (detailData: any) => void;
  duringFirstOrder: boolean;
  setDuringFirstOrder: (duringFirstOrder: boolean) => void;
  reQueryDetailData: ({
    orderId,
    validate,
    clearDateAndUpdate,
    setMyRate
  }: {
    orderId: string;
    validate?: boolean;
    clearDateAndUpdate?: boolean;
    setMyRate?: boolean;
  }) => Promise<any>;
  isLoadingData: boolean;
  setIsLoadingData: (isLoadingData: boolean) => void;
  myRateLoading: boolean;
  setMyRateLoading: (myRateLoading: boolean) => void;
  titleType: string;
  setTitleType: (titleType: string) => void;
};

export const useDetailDataStore = create<DetailDataStore>((set) => ({
  isLoadingData: true,
  setIsLoadingData: (isLoadingData: boolean) => set({ isLoadingData }),
  myRateLoading: false,
  setMyRateLoading: (myRateLoading: boolean) => set({ myRateLoading }),
  detailData: {},
  setDetailData: (detailData: any) => set({ detailData }),
  duringFirstOrder: true,
  setDuringFirstOrder: (duringFirstOrder: boolean) => set({ duringFirstOrder }),
  reQueryDetailData,
  titleType: '',
  setTitleType: (titleType: string) => set({ titleType })
}));

const getGrowthbookFlag = (detailData: any) => {
  if (detailData?.order?.campaignId && detailData?.order?._id) {
    growthbook.setAttributes({
      id: detailData.order.campaignId + detailData.order._id,
      url: window.location.href,
      path: window.location.pathname,
      host: window.location.host,
      query: window.location.search,
      deviceType: 'foo',
      browser: navigator.userAgent,
      utmSource: 'foo',
      utmMedium: 'foo',
      utmCampaign: 'foo',
      utmTerm: 'foo',
      utmContent: 'foo',
      platform_user_id: detailData?.user?.__id || 'foo',
      campaign_id: detailData.order.campaignId
    });
  }
};
